import React, { useState, useEffect } from "react";
import Snackbar from "@mui/material/Snackbar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import { useTheme } from "@mui/material/styles";
import { 
  IconCheck, 
  IconAlertCircle, 
  IconAlertTriangle, 
  IconInfoCircle, 
  IconX 
} from "@tabler/icons-react";

function TransitionSlide(props) {
  return <Slide {...props} direction="down" timeout={{ enter: 220, exit: 180 }} />;
}

function GlobalSnackbar() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    title: "",
    message: "",
  });

  useEffect(() => {
    const handleShowSnackbar = (event) => {
      const { message, color, title } = event.detail;
      const severityMap = {
        success: "success",
        error: "error",
        warning: "warning",
        info: "info",
      };
      setToast({
        open: true,
        severity: severityMap[color] || "success",
        title: title || (color === "error" ? "Error" : color === "warning" ? "Warning" : "Notification"),
        message: message || "",
      });
    };

    window.addEventListener("show-snackbar", handleShowSnackbar);
    return () => window.removeEventListener("show-snackbar", handleShowSnackbar);
  }, []);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  const getSeverityStyle = (severity) => {
    if (isDark) {
      const raisedBg = theme.palette.background?.raised || '#272B33';
      const borderDef = theme.palette.border?.default || 'rgba(255, 255, 255, 0.08)';
      switch (severity) {
        case 'error':
          return {
            borderColor: borderDef,
            borderLeftColor: '#F87171',
            bgColor: raisedBg,
            titleColor: theme.palette.text?.primary || '#ECEEF1',
            msgColor: theme.palette.text?.secondary || '#A4ABB8',
            iconColor: '#F87171',
            iconBg: 'rgba(248, 113, 113, 0.12)',
            icon: <IconAlertCircle size={20} stroke={1.75} />
          };
        case 'warning':
          return {
            borderColor: borderDef,
            borderLeftColor: '#FBBF24',
            bgColor: raisedBg,
            titleColor: theme.palette.text?.primary || '#ECEEF1',
            msgColor: theme.palette.text?.secondary || '#A4ABB8',
            iconColor: '#FBBF24',
            iconBg: 'rgba(251, 191, 36, 0.12)',
            icon: <IconAlertTriangle size={20} stroke={1.75} />
          };
        case 'info':
          return {
            borderColor: borderDef,
            borderLeftColor: '#60A5FA',
            bgColor: raisedBg,
            titleColor: theme.palette.text?.primary || '#ECEEF1',
            msgColor: theme.palette.text?.secondary || '#A4ABB8',
            iconColor: '#60A5FA',
            iconBg: 'rgba(96, 165, 250, 0.12)',
            icon: <IconInfoCircle size={20} stroke={1.75} />
          };
        case 'success':
        default:
          return {
            borderColor: borderDef,
            borderLeftColor: '#34D399',
            bgColor: raisedBg,
            titleColor: theme.palette.text?.primary || '#ECEEF1',
            msgColor: theme.palette.text?.secondary || '#A4ABB8',
            iconColor: '#34D399',
            iconBg: 'rgba(52, 211, 153, 0.12)',
            icon: <IconCheck size={20} stroke={1.75} />
          };
      }
    }

    // Light mode original styles
    switch (severity) {
      case 'error':
        return {
          borderColor: '#e2e8f0',
          borderLeftColor: '#ef4444',
          bgColor: '#ffffff',
          titleColor: '#0f172a',
          msgColor: '#475569',
          iconColor: '#dc2626',
          iconBg: 'rgba(239, 68, 68, 0.08)',
          icon: <IconAlertCircle size={20} stroke={1.75} />
        };
      case 'warning':
        return {
          borderColor: '#e2e8f0',
          borderLeftColor: '#ea580c',
          bgColor: '#ffffff',
          titleColor: '#0f172a',
          msgColor: '#475569',
          iconColor: '#ea580c',
          iconBg: 'rgba(234, 88, 12, 0.08)',
          icon: <IconAlertTriangle size={20} stroke={1.75} />
        };
      case 'info':
        return {
          borderColor: '#e2e8f0',
          borderLeftColor: '#2563eb',
          bgColor: '#ffffff',
          titleColor: '#0f172a',
          msgColor: '#475569',
          iconColor: '#2563eb',
          iconBg: 'rgba(37, 99, 235, 0.08)',
          icon: <IconInfoCircle size={20} stroke={1.75} />
        };
      case 'success':
      default:
        return {
          borderColor: '#e2e8f0',
          borderLeftColor: '#1e293b',
          bgColor: '#ffffff',
          titleColor: '#1e293b',
          msgColor: '#1e293b',
          iconColor: '#1e293b',
          iconBg: 'rgba(30, 41, 59, 0.08)',
          icon: null
        };
    }
  };

  const currentTheme = getSeverityStyle(toast.severity);

  return (
    <Snackbar
      open={toast.open}
      autoHideDuration={5000}
      onClose={handleClose}
      TransitionComponent={TransitionSlide}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      sx={{ mt: 7.5, mr: 2.5, zIndex: 9999 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          minWidth: '280px',
          maxWidth: '380px',
          padding: '8px 12px',
          backgroundColor: currentTheme.bgColor,
          borderRadius: '8px',
          border: `1px solid ${currentTheme.borderColor}`,
          borderLeft: `4px solid ${currentTheme.borderLeftColor}`,
          boxShadow: isDark ? '0 12px 32px rgba(0, 0, 0, 0.45)' : '0 4px 12px -2px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.03)',
          position: 'relative'
        }}
      >
        {/* Outline Icon Container */}
        {currentTheme.icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '6px',
              backgroundColor: currentTheme.iconBg,
              color: currentTheme.iconColor,
              flexShrink: 0
            }}
          >
            {currentTheme.icon}
          </Box>
        )}

        {/* Content Typography */}
        <Box flex={1} pr={2.5}>
          {toast.title && (
            <Typography variant="subtitle2" fontWeight="500" color={currentTheme.titleColor} fontSize="13.5px" lineHeight={1.25}>
              {toast.title}
            </Typography>
          )}
          <Typography variant="body2" fontWeight="400" color={currentTheme.msgColor} fontSize="12.5px" lineHeight={1.35} sx={{ mt: toast.title ? 0.25 : 0 }}>
            {toast.message}
          </Typography>
        </Box>

        {/* Minimal Compact Close X Icon */}
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: isDark ? (theme.palette.text?.placeholder || '#707886') : '#94a3b8',
            padding: '4px',
            borderRadius: '6px',
            flexShrink: 0,
            '&:hover': { 
              color: isDark ? (theme.palette.text?.primary || '#ECEEF1') : '#334155', 
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)' 
            }
          }}
        >
          <IconX size={16} stroke={1.75} />
        </IconButton>
      </Box>
    </Snackbar>
  );
}

export default GlobalSnackbar;
